import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory, useLocation } from 'react-router-dom';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { useTheme } from '../../context/ThemeContext';
import {
  getCategoryById,
  getLocationLabel,
  getSubcategory,
  jamaicaLocations,
  marketplaceBrowseCopy,
  marketplaceCategories,
} from '../../config/marketplaceCategories';
import { makeGetListingsByIdSelector } from '../../ducks/marketplaceData.duck';
import { isUserAuthorized } from '../../util/userHelpers';
import { requestProgressiveAuth } from '../../util/authIntent';
import { createSlug } from '../../util/urlHelpers';
import { createResourceLocatorString } from '../../util/routes';
import { getInquiryProcessAlias } from '../../util/inquiryProcess';
import { sendInquiry } from '../ListingPage/ListingPage.duck';
import {
  fetchLandingAssetListings,
  resetLandingAssetListings,
} from '../../ducks/landingAssetListings.duck';

import CategoryIcon from './CategoryIcon';
import MarketplaceBrowseSidebar from './MarketplaceBrowseSidebar';
import { IconSpinner, ListingCardThumbnail, NamedLink, ResponsiveImage, SecondaryButtonInline } from '../../components';

import css from './AssetMarketBrowse.module.css';

const DEFAULT_INQUIRY_MESSAGE = 'Hi — I have a question about this listing.';
const FEATURED_COUNT = 4;

const BrowseListingThumb = ({ listing, className }) => {
  const title = listing?.attributes?.title || '';
  const firstImage = listing?.images?.[0] || null;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants || {}).filter(k =>
        k.startsWith('listing-card')
      )
    : [];

  if (firstImage && variants.length > 0) {
    return (
      <ResponsiveImage
        rootClassName={className}
        alt={title}
        image={firstImage}
        variants={variants}
        sizes="120px"
      />
    );
  }

  return (
    <ListingCardThumbnail
      listingTitle={title}
      width={4}
      height={3}
      className={className}
    />
  );
};

const listingLocationId = listing => {
  const pub = listing?.attributes?.publicData || {};
  return pub.jamaicaLocation || pub.locationId || pub.city || null;
};

const listingCategoryId = listing => {
  const pub = listing?.attributes?.publicData || {};
  return pub.marketCategory || pub.category || 'for-sale';
};

const formatPrice = listing => {
  const price = listing?.attributes?.price;
  if (!price) {
    return 'Contact';
  }
  const amount = price.amount / 100;
  const prefix = price.currency === 'USD' ? 'US$' : price.currency;
  return `${prefix}${amount % 1 === 0 ? amount.toFixed(0) : amount.toFixed(2)}`;
};

const AssetMarketBrowse = ({ className, rootClassName, compact = false }) => {
  const { isDark } = useTheme();
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const currentUser = useSelector(state => state.user.currentUser);

  const [areaId, setAreaId] = useState('all');
  const [activeCategoryId, setActiveCategoryId] = useState(null);
  const [activeSubcategoryId, setActiveSubcategoryId] = useState(null);
  const [inquiryListingId, setInquiryListingId] = useState(null);

  const getListingsByIdSelector = useMemo(() => makeGetListingsByIdSelector(), []);
  const { listingIds, fetched, inProgress, error, forbidden } = useSelector(
    state => state.landingAssetListings || {}
  );
  const listings = useSelector(state => getListingsByIdSelector(state, listingIds || []));

  useEffect(() => {
    if (!fetched && !inProgress) {
      dispatch(fetchLandingAssetListings({ listingFields: config.listing.listingFields }));
    }
  }, [config.listing.listingFields, dispatch, fetched, inProgress]);

  const filteredListings = useMemo(() => {
    let result = [...listings];
    if (areaId !== 'all') {
      result = result.filter(l => {
        const loc = listingLocationId(l);
        return !loc || loc === areaId;
      });
    }
    if (activeCategoryId) {
      result = result.filter(l => listingCategoryId(l) === activeCategoryId);
    }
    return result;
  }, [listings, areaId, activeCategoryId]);

  const featuredListings = filteredListings.slice(0, FEATURED_COUNT);
  const recentListings = filteredListings.slice(FEATURED_COUNT);

  const activeCategory = getCategoryById(activeCategoryId);
  const activeSubcategory = getSubcategory(activeCategoryId, activeSubcategoryId);
  const areaLabel = getLocationLabel(areaId);

  const handleSubcategoryClick = (categoryId, subcategoryId) => {
    setActiveCategoryId(categoryId);
    setActiveSubcategoryId(subcategoryId);
  };

  const clearFilters = () => {
    setActiveCategoryId(null);
    setActiveSubcategoryId(null);
  };

  const handleCreateListing = e => {
    e.preventDefault();
    if (!isUserAuthorized(currentUser)) {
      requestProgressiveAuth({
        history,
        routeConfiguration,
        location,
        intent: 'postListing',
        onAuthenticated: () => {
          history.push(createResourceLocatorString('NewListingPage', routeConfiguration, {}, {}));
        },
      });
      return;
    }
    history.push(createResourceLocatorString('NewListingPage', routeConfiguration, {}, {}));
  };

  const handleInquire = listing => {
    const listingTypes = config.listing.listingTypes || [];
    const inquiryAlias = getInquiryProcessAlias(listing, listingTypes);

    if (!inquiryAlias) {
      history.push(
        createResourceLocatorString(
          'ListingPage',
          routeConfiguration,
          { id: listing.id.uuid, slug: createSlug(listing.attributes?.title || '') },
          {}
        )
      );
      return;
    }

    if (!isUserAuthorized(currentUser)) {
      requestProgressiveAuth({
        history,
        routeConfiguration,
        location,
        intent: 'inquireListing',
        onAuthenticated: () => {
          setInquiryListingId(listing.id.uuid);
          dispatch(sendInquiry(listing, DEFAULT_INQUIRY_MESSAGE, listingTypes)).finally(() =>
            setInquiryListingId(null)
          );
        },
      });
      return;
    }

    setInquiryListingId(listing.id.uuid);
    dispatch(sendInquiry(listing, DEFAULT_INQUIRY_MESSAGE, listingTypes)).finally(() =>
      setInquiryListingId(null)
    );
  };

  const rootClass = classNames(
    rootClassName || css.root,
    compact ? css.rootCompact : null,
    isDark ? null : css.rootLight,
    className
  );

  const visibleCategories = compact ? marketplaceCategories.slice(0, 6) : marketplaceCategories;
  const hasActiveFilter = Boolean(activeCategoryId || areaId !== 'all');

  const categoryGrid = (
    <div className={classNames(css.categoryGrid, compact ? css.categoryGridCompact : null)}>
      {visibleCategories.map(category => (
        <div key={category.id} className={css.categoryColumn}>
          <div className={css.categoryHeader}>
            <span
              className={classNames(
                css.kindDot,
                category.postKind === 'on-system' ? css.kindDotOnSystem : css.kindDotOffSystem
              )}
              title={
                category.postKind === 'on-system'
                  ? marketplaceBrowseCopy.sidebar.legendOnSystem
                  : marketplaceBrowseCopy.sidebar.legendOffSystem
              }
              aria-hidden="true"
            />
            <CategoryIcon name={category.icon} size={13} className={css.categoryIcon} />
            <span className={css.categoryTitle}>{category.label}</span>
          </div>
          <ul className={css.subcategoryList}>
            {category.subcategories.map(sub => (
              <li key={sub.id}>
                <button
                  type="button"
                  className={classNames(
                    css.subcategoryLink,
                    activeCategoryId === category.id && activeSubcategoryId === sub.id
                      ? css.subcategoryActive
                      : null
                  )}
                  onClick={() => handleSubcategoryClick(category.id, sub.id)}
                >
                  {sub.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );

  const mainBody = (
    <>
      {!compact && (activeCategory || areaId !== 'all') ? (
        <div className={css.filterBar}>
          <span className={css.filterText}>
            {areaLabel}
            {activeSubcategory
              ? ` · ${activeSubcategory.label}`
              : activeCategory
                ? ` · ${activeCategory.label}`
                : ''}
          </span>
          <button type="button" className={css.clearFilter} onClick={clearFilters}>
            clear
          </button>
        </div>
      ) : null}

      {categoryGrid}

      {!compact && featuredListings.length > 0 ? (
        <section className={css.featuredSection} aria-label={marketplaceBrowseCopy.featuredLabel}>
          <h3 className={css.sectionHeading}>{marketplaceBrowseCopy.featuredLabel}</h3>
          <ul className={css.featuredRow}>
            {featuredListings.map(listing => {
              const slug = createSlug(listing.attributes?.title || '');
              const listingUrl = createResourceLocatorString(
                'ListingPage',
                routeConfiguration,
                { id: listing.id.uuid, slug },
                {}
              );
              return (
                <li key={listing.id.uuid} className={css.featuredCard}>
                  <Link to={listingUrl} className={css.featuredLink}>
                    <div className={css.featuredThumb}>
                      <BrowseListingThumb listing={listing} className={css.featuredThumbImg} />
                    </div>
                    <div className={css.featuredBody}>
                      <span className={css.featuredPrice}>{formatPrice(listing)}</span>
                      <span className={css.featuredTitle}>{listing.attributes?.title}</span>
                    </div>
                  </Link>
                  <button
                    type="button"
                    className={css.featuredInquire}
                    onClick={() => handleInquire(listing)}
                    disabled={inquiryListingId === listing.id.uuid}
                  >
                    Inquire
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      {!compact ? (
        <section className={css.recentSection} aria-label={marketplaceBrowseCopy.recentLabel}>
          <h3 className={css.sectionHeading}>{marketplaceBrowseCopy.recentLabel}</h3>

          {!fetched || (inProgress && listings.length === 0) ? (
            <div className={css.loading}>
              <IconSpinner />
            </div>
          ) : forbidden ? (
            <div className={css.empty}>
              <NamedLink name="LoginPage">{marketplaceBrowseCopy.postListingCta}</NamedLink>
            </div>
          ) : error ? (
            <div className={css.empty}>
              <SecondaryButtonInline onClick={() => dispatch(resetLandingAssetListings())}>
                Retry
              </SecondaryButtonInline>
            </div>
          ) : recentListings.length === 0 && featuredListings.length === 0 ? (
            <p className={css.empty}>{marketplaceBrowseCopy.emptyFiltered}</p>
          ) : (
            <ul className={css.recentList}>
              {(recentListings.length ? recentListings : filteredListings).map(listing => {
                const catId = listingCategoryId(listing);
                const cat = getCategoryById(catId) || marketplaceCategories[0];
                const slug = createSlug(listing.attributes?.title || '');
                const listingUrl = createResourceLocatorString(
                  'ListingPage',
                  routeConfiguration,
                  { id: listing.id.uuid, slug },
                  {}
                );
                return (
                  <li key={listing.id.uuid} className={css.recentRow}>
                    <CategoryIcon name={cat.icon} size={12} className={css.recentIcon} />
                    <Link to={listingUrl} className={css.recentTitle}>
                      {listing.attributes?.title}
                    </Link>
                    <span className={css.recentPrice}>{formatPrice(listing)}</span>
                    <span className={css.recentArea}>{areaLabel}</span>
                    <button
                      type="button"
                      className={css.recentInquire}
                      onClick={() => handleInquire(listing)}
                    >
                      Inquire
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      ) : null}

      {compact && featuredListings.length > 0 ? (
        <ul className={css.compactFeatured}>
          {featuredListings.slice(0, 3).map(listing => {
            const slug = createSlug(listing.attributes?.title || '');
            const listingUrl = createResourceLocatorString(
              'ListingPage',
              routeConfiguration,
              { id: listing.id.uuid, slug },
              {}
            );
            return (
              <li key={listing.id.uuid}>
                <Link to={listingUrl} className={css.compactFeaturedLink}>
                  <BrowseListingThumb listing={listing} className={css.compactThumb} />
                  <span>{listing.attributes?.title}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      ) : null}
    </>
  );

  return (
    <section className={rootClass} data-testid="asset-market-browse">
      <div className={css.toolbar}>
        <div className={css.toolbarLeft}>
          {!compact ? (
            <>
              <h2 className={css.title}>{marketplaceBrowseCopy.title}</h2>
              <p className={css.subtitle}>{marketplaceBrowseCopy.subtitle}</p>
            </>
          ) : (
            <h2 className={css.titleCompact}>{marketplaceBrowseCopy.title}</h2>
          )}
        </div>
        {compact ? (
          <div className={css.toolbarRight}>
            <label className={css.areaLabel} htmlFor="marketplace-area-select">
              {marketplaceBrowseCopy.locationLabel}
            </label>
            <select
              id="marketplace-area-select"
              className={css.areaSelect}
              value={areaId}
              onChange={e => setAreaId(e.target.value)}
            >
              {jamaicaLocations.map(loc => (
                <option key={loc.id} value={loc.id}>
                  {loc.label}
                </option>
              ))}
            </select>
            <Link to="/marketplace" className={css.browseAllLink}>
              {marketplaceBrowseCopy.browseAllLink} →
            </Link>
          </div>
        ) : null}
      </div>

      {!compact ? (
        <div className={css.layout}>
          <MarketplaceBrowseSidebar
            areaId={areaId}
            onAreaChange={setAreaId}
            onClearFilters={clearFilters}
            onPostAd={handleCreateListing}
            hasActiveFilter={hasActiveFilter}
          />
          <div className={css.main}>{mainBody}</div>
        </div>
      ) : (
        mainBody
      )}
    </section>
  );
};

export default AssetMarketBrowse;
